import styled, { css } from 'styled-components';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import MQ from '@rover/kibble/styles/MediaQuery';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

type DatePickerStylingWrapperProps = {
  readOnly?: boolean;
  styleAsError?: boolean;
  calendarDropUp?: boolean;
  calendarHeightOffset?: number | null;
  calendarInset?: number;
  calendarIsOpen?: boolean;
  newCalendarInteraction?: boolean;
  isMobileBrowser?: boolean;
  hideRangeConnector?: boolean;
};

const getBorderForWrapper = (
  newCalendarInteraction: boolean,
  styleAsError: boolean,
  { borderWidth, colors, sizes }: RoverTheme,
  specifiedBorderWidth: keyof RoverTheme['borderWidth'] = 'primary'
): string => {
  if (newCalendarInteraction) {
    return sizes['0x'];
  }

  const borderColor = styleAsError ? colors.border.error : colors.border.primary;

  return `${borderWidth[specifiedBorderWidth]} solid ${borderColor}`;
};

// eslint-disable-next-line rover/prefer-kibble-components
const DatePickerStylingWrapper = styled.div<DatePickerStylingWrapperProps>`
  position: relative;

  input {
    height: 40px;
    width: 100%;
    border-radius: ${({ theme }) => theme.radii.secondary};
    padding: 8px;
    transition: border-color ease-in-out 0.15s;
    cursor: ${({ readOnly }) => (readOnly ? 'pointer' : 'text')};
    border: ${({ theme, styleAsError, newCalendarInteraction }) =>
      getBorderForWrapper(!!newCalendarInteraction, !!styleAsError, theme as RoverTheme)};

    ${MQ.XS.toString()} {
      font-size: ${({ theme }) => theme.text['200'].fontSize};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.border.input_focus};
    }

    &:disabled {
      font-weight: 500;
      opacity: 1;
    }
  }

  .DayPickerInput {
    width: 100%;

    &-Overlay {
      opacity: ${({ calendarIsOpen }) =>
        calendarIsOpen || typeof calendarIsOpen === 'undefined' ? 'inherit' : '0'};
      z-index: ${ZIndex.DROPDOWN.toString()};
      position: absolute;
      left: ${({ calendarInset }) => `-${calendarInset || 0}px`};
      ${({ calendarDropUp, calendarHeightOffset }) =>
        calendarDropUp &&
        css`
          bottom: 100%;
          margin-bottom: ${calendarHeightOffset || 0}px;
        `};
    }
  }

  .DayPicker {
    width: ${({ newCalendarInteraction }) => (newCalendarInteraction ? '100%' : 'unset')};

    &-wrapper {
      border-radius: ${({ isMobileBrowser }) => (isMobileBrowser ? '0px' : '4px')};
      border: ${({ newCalendarInteraction, styleAsError, theme }) =>
        getBorderForWrapper(
          !!newCalendarInteraction,
          !!styleAsError,
          theme as RoverTheme,
          'secondary'
        )};
      position: relative;
      z-index: 1;
    }

    &-Caption > div {
      color: ${({ theme }) => theme.textColor.primary};
      font-size: 16px;
      font-weight: bold;
    }

    &-Month {
      display: ${({ newCalendarInteraction, isMobileBrowser }) =>
        newCalendarInteraction && !isMobileBrowser ? 'block' : 'table'};
      border-collapse: ${({ newCalendarInteraction }) =>
        newCalendarInteraction ? 'unset' : 'collapse'};
      border-spacing: ${({ newCalendarInteraction }) => (newCalendarInteraction ? '1px' : '0px')};
      width: ${({ newCalendarInteraction, isMobileBrowser }) =>
        newCalendarInteraction && isMobileBrowser ? '100%' : 'unset'};
    }

    &-Weekday {
      color: ${({ theme }) => theme.textColor.primary};
      font-size: 13px;
    }

    &-Day {
      border-radius: 0;
      height: ${({ newCalendarInteraction, isMobileBrowser }) =>
        newCalendarInteraction && isMobileBrowser ? '50px' : '40px'};
      width: ${({ newCalendarInteraction, isMobileBrowser }) =>
        newCalendarInteraction && isMobileBrowser ? '50px' : '40px'};
      min-width: ${({ newCalendarInteraction, isMobileBrowser }) =>
        newCalendarInteraction && isMobileBrowser ? '50px' : '40px'};
      color: ${({ theme }) => theme.textColor.secondary};
      font-size: ${({ theme }) => theme.text['100'].fontSize};
      font-weight: ${({ newCalendarInteraction }) => (newCalendarInteraction ? '700' : 'normal')};
      aspect-ratio: ${({ newCalendarInteraction, isMobileBrowser }) =>
        newCalendarInteraction && isMobileBrowser ? '1' : 'unset'};
      position: relative;

      // Styles for days that are not disabled, outside the current month, or part of a selected range
      &:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside):not(.DayPicker-Day--between):not(
          .DayPicker-Day--start
        ):not(.DayPicker-Day--end):not(.DayPicker-Day--selected):hover {
        background-color: ${({ newCalendarInteraction, theme }) =>
          newCalendarInteraction
            ? `${theme.background.primary} !important`
            : `${theme.background.interactive.primary_pressed} !important`};
        border-radius: ${({ theme, newCalendarInteraction }) =>
          newCalendarInteraction ? theme.radii.primary : theme.radii.round};
      }

      // Styles for selected days, start/end of range
      &--start,
      &--between,
      &--end,
      &--selected {
        &:not(.DayPicker-Day--outside):not(.DayPicker-Day--disabled) {
          background-color: ${({ newCalendarInteraction, theme }) =>
            newCalendarInteraction
              ? `${theme.background.primary} !important`
              : `${theme.background.interactive.button_primary_base} !important`};
          border-radius: ${({ theme, newCalendarInteraction }) =>
            newCalendarInteraction ? theme.radii.primary : theme.radii.round};
          color: ${({ newCalendarInteraction, theme }) =>
            newCalendarInteraction
              ? `${theme.textColor.primary} !important`
              : `${theme.textColor.primary_inverse} !important`};
          border: ${({ newCalendarInteraction, theme }) =>
            newCalendarInteraction
              ? `2px solid ${theme.background.interactive.button_primary_base}`
              : '0'};

          &:hover {
            background-color: ${({ newCalendarInteraction, theme }) =>
              newCalendarInteraction
                ? `${theme.background.primary} !important`
                : `${theme.colors.interactive_link.primary_pressed} !important`};
            color: ${({ newCalendarInteraction, theme }) =>
              newCalendarInteraction
                ? `${theme.textColor.primary} !important`
                : `${theme.textColor.primary_inverse} !important`};
          }
        }
      }

      // A contiguous selection is drawn as one range by a bar sitting behind the
      // days, which only reads correctly where selected days are solid circles.
      // Callers whose days are rounded squares of their own opt out of the bar and
      // let every day, endpoints and days in between alike, keep its own shape.
      ${({ hideRangeConnector, theme }) =>
        !hideRangeConnector &&
        css`
          &--selected {
            &.DayPicker-Day--start,
            &.DayPicker-Day--end,
            &.DayPicker-Day--between {
              &:after {
                background-color: ${theme.colors.interactive_text.primary_active} !important;
                bottom: 0;
                content: '';
                left: 0;
                position: absolute;
                right: 0;
                top: 0;
                z-index: -1;
                height: 100%;
                width: 100%;
              }
            }

            &.DayPicker-Day--start {
              &:after {
                left: 50%;
                width: 50%;
              }
            }

            &.DayPicker-Day--end {
              &:after {
                width: 50%;
              }
            }

            &.DayPicker-Day--between {
              border-radius: 0;
              &:hover {
                border-radius: ${theme.radii.round};
              }
            }
          }
        `};

      // Styles for disabled days
      &--disabled {
        color: ${({ theme }) => theme?.colors?.interactive_text.disabled};
      }

      // Styles for holiday date
      &--holiday {
        &:not(.DayPicker-Day--between):not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
          border-radius: 50%;
          background: ${({ theme }) => theme?.background?.highlight};
        }
      }
    }
  }
`;

export default DatePickerStylingWrapper;
